<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import UIKit, { type ITableSortSettings, type ITableColumn } from '@pocketprep/ui-kit'
import SeatsTable from '@/components/Admin/SeatsTable.vue'
import EmailTextarea from '@/components/Admin/EmailTextarea.vue'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'
import { licenseModule } from '@/store/license/module'
import { studentModule } from '@/store/student/module'
import { userModule } from '@/store/user/module'
import { toastModule } from '@/store/toast/module'
import * as Sentry from '@sentry/vue'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'

type TEmail = {
    email: string
    isValidFormat: boolean
    isAlreadyUsed: boolean
}

type TGroupOption = {
    value: string
    label: string
    libraryKey?: string
    instructor?: string
}

const {
    SidePanel,
    Button: PocketButton,
    Radio: PocketRadio,
    Select: PocketSelect,
    Input: PocketInput,
    Textarea: PocketTextarea,
    Table,
    Icon,
    Errors,
    ToggleSwitch,
} = UIKit

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const isLoading = ref(false)
const customMessage = ref('')
const activeTabName = ref('Select Exam')
const tabs = [
    'Select Exam',
    'Add Emails',
    'Personalize & Preview',
]
const examColumns = ref<ITableColumn[]>([{
    name: 'Exam Prep',
    propName: 'name',
    styles: {
        width: '240px',
    },
    labelStyles: {
        justifyContent: 'flex-start',
    },
}, {
    name: 'Available<br>Seats',
    propName: 'available',
    styles: {
        width: '64px',
    },
}, {
    name: 'Pending<br>Seats',
    propName: 'pending',
    styles: {
        width: '64px',
    },
}, {
    name: 'Active<br>Seats',
    propName: 'active',
    styles: {
        width: '64px',
    },
}])
const groupTypeOptions = [
    { label: 'Add to Existing Group', value: 'existingGroup' },
    { label: 'Create New Group', value: 'newGroup' },
]
const selectedExamIds = ref<string[]>([])
const selectedInstructor = ref<null | { value: string; label: string }>(null)
const selectedGroup = ref<null | { value: string; label: string }>(null)
const newGroupName = ref<string | null>(null)
const isDuplicateGroup = ref(false)
const emails = ref<TEmail[]>([])
const sortSettings = ref<ITableSortSettings>({ column: examColumns.value[0], direction: 1 })
const addToGroup = ref(false)
const selectGroupType = ref<null | TGroupOption>(null)
const usePermission = ref<null | { value: 1 | 2; label: string }>(null)
const editPermission = ref<null | { value: 1 | 2; label: string }>(null)
const showPurchaseSeatsModal = ref(false)

const closePurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = false
}

const openPurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = true
}

const clearIsDuplicateGroup = () => {
    isDuplicateGroup.value = false
}

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const orgExamIdGuidLib = computed(() => {
    return examModule.getters.getOrgExamIdGuidLib()
})

const groupsByCompositeKey = computed(() => {
    return groupModule.getters.getGroupsByGroupCompositeKey()
})

const usedEmails = computed(() => {
    const selectedExamGuids = selectedExamIds.value
        .map(id => examMetadata.value[currentOrgExams.value[id]?.examMetadata.objectId || '']?.examGuid)
        .filter(exam => exam)
    return selectedExamGuids.length
        ? currentOrgLicenses.value.reduce((acc, license) => {
            if (license.examGuid && selectedExamGuids.includes(license.examGuid) && license.userEmail) {
                acc.push(license.userEmail)
            }
            return acc
        }, [] as string[])
        : []
})

const validEmails = computed(() => {
    return emails.value.filter(o => o.isValidFormat && !o.isAlreadyUsed).map(o => o.email)
})

const emailValidationErrors = computed(() => {
    const errors: string[] = []
    if (emails.value.filter(e => !e.isValidFormat).length > 0) {
        errors.push('Invalid email address. Required format is name@domain.com.')
    }
    if (emails.value.filter(e => e.isAlreadyUsed).length > 0) {
        errors.push('Invalid email address. Remove previously invited students.')
    }
    return errors
})

const examLicenseMetrics = computed(() => {
    return currentOrgLicenses.value.reduce(
        (acc, license) => {
            if (!license.examGuid) {
                return acc
            }
            if (!(license.examGuid in acc)) {
                acc[license.examGuid] = {
                    available: 0 - validEmails.value.length,
                    pending: 0,
                    active: 0,
                }
            }
            const examLicense = acc[license.examGuid]
            if (!examLicense) {
                return acc
            }
            if (license.status === 'available') {
                examLicense.available++
            } else if (license.status === 'pending') {
                examLicense.pending++
            } else if (license.status === 'consumed') {
                examLicense.active++
            }
            return acc
        },
        {} as { [key: string]: { available: number; pending: number; active: number } }
    )
})

const mappedExams = computed(() => {
    const sortSettingsVal = sortSettings.value
    return Object.values(currentOrgExams.value)
        .filter(e => e && selectedExamIds.value.includes(e.objectId))
        .map(e => {
            const meta = e && examMetadata.value?.[e.examMetadata.objectId]
            const examNativeAppName = meta?.nativeAppName
            const displayName = (e && e.displayName) ? e.displayName : examNativeAppName
            const examMetrics = meta?.examGuid && examLicenseMetrics.value[meta.examGuid]
                || { available: 0, pending: 0, active: 0 }
            if (!displayName) {
                return undefined
            }
            const pending = examMetrics.pending + validEmails.value.length
                + (!meta?.isFree && examMetrics.available < 0 ? examMetrics.available : 0)
            const available = meta?.isFree ? '∞' : Math.max(0, examMetrics.available)
            return {
                id: e && e.objectId,
                name: displayName,
                available,
                pending,
                active: examMetrics.active,
                pendingCount: examMetrics.pending,
                activeCount: examMetrics.active,
                availableCount: meta?.isFree ? Infinity : examMetrics.available,
                nameText: (e && e.displayName) || examNativeAppName || '',
            }
        })
        .filter((e): e is NonNullable<typeof e> => !!e)
        .sort((a, b) => {
            if (sortSettingsVal?.column && a && b) {
                if (sortSettingsVal.column.name === 'Exam Prep') {
                    return sortSettingsVal.direction * a.nameText.localeCompare(b.nameText)
                }
                if (sortSettingsVal.column.name === 'Available<br>Seats') {
                    return sortSettingsVal.direction * (b.availableCount - a.availableCount)
                }
                if (sortSettingsVal.column.name === 'Pending<br>Seats') {
                    return sortSettingsVal.direction * (b.pendingCount - a.pendingCount)
                }
                if (sortSettingsVal.column.name === 'Active<br>Seats') {
                    return sortSettingsVal.direction * (b.activeCount - a.activeCount)
                }
            }
            return -1
        })
})

const remainingLicenses = computed(() => {
    return mappedExams.value.reduce((acc, m) => m?.availableCount ? acc + m.availableCount : acc, 0)
})

const newStudentsCount = computed(() => {
    return validEmails.value.length + (remainingLicenses.value < 0 ? remainingLicenses.value : 0)
})

const disabledExamIds = computed(() => {
    if (currentOrgInstructor.value?.isAdmin) {
        return []
    }
    return Object.values(currentOrgExams.value)
        .filter(e => e && !yourOrgExamIds.value.includes(e.objectId)).map(e => e?.objectId)
})

const availableInstructorIds = computed(() => {
    if (!currentOrgGroups.value) {
        return []
    }
    const instructorExamMap = Object.values(currentOrgGroups.value).reduce((acc, g) => {
        if (g.type !== 'system' || !g.owner) {
            return acc
        }
        if (!(g.owner.objectId in acc)) {
            acc[g.owner.objectId] = []
        }
        acc[g.owner.objectId]?.push(g.orgExam.objectId)
        return acc
    }, {} as { [key: string]: string[] })
    return Object.entries(instructorExamMap).reduce((acc, [ instructorId, exams ]) => {
        if (selectedExamIds.value.filter(id => exams.includes(id)).length === selectedExamIds.value.length) {
            acc.push(instructorId)
        }
        return acc
    }, [] as string[])
})

const instructorOptions = computed(() => {
    if (!currentOrgInstructors.value || !currentOrgInstructorUsers.value) {
        return []
    }
    return Object.values(currentOrgInstructors.value)
        .filter(i => availableInstructorIds.value.includes(i.objectId))
        .map(i => {
            const user = currentOrgInstructorUsers.value?.[i.user.objectId]
            if (!user) {
                return undefined
            }
            return {
                value: i.objectId,
                label: (user.firstName || user.lastName) ? [ user.firstName, user.lastName ].join(' ') : user.email,
            }
        })
        .filter((option): option is { value: string; label: string } => !!option)
})

const groupOptions = computed(() => {
    return Object.values(currentOrgGroups.value).reduce((acc, orgGroup) => {
        const instructorId = orgGroup.owner?.objectId
        if (
            orgGroup.orgExam.objectId === selectedExamIds.value[0]
            && (
                orgGroup.type === 'public'
                || (orgGroup.type === 'private' && instructorId === currentOrgInstructor.value?.objectId)
            )
        ) {
            const name = groupModule.getters.getGroupName(orgGroup.objectId)
            const examMetadataId = currentOrgExams.value[orgGroup.orgExam.objectId]?.examMetadata.objectId
            const examGuid = examMetadataId ? examMetadata.value[examMetadataId]?.examGuid : undefined
            if (!name || !examGuid) {
                return acc
            }
            const libraryKey = groupModule.getters.getGroupCompositeKeyForGroupId(orgGroup.objectId)
            if (acc.find(groupOption => groupOption.libraryKey === libraryKey)) {
                return acc
            }
            if (!instructorId) {
                return acc
            }
            const instructor = currentOrgInstructors.value?.[instructorId]
            if (!instructor) {
                return acc
            }
            const instructorUser = currentOrgInstructorUsers.value?.[instructor.user.objectId]
            acc.push({
                value: orgGroup.objectId,
                label: orgGroup.name,
                libraryKey,
                instructor: `${instructorUser?.firstName} ${instructorUser?.lastName}`,
            })
        }
        return acc
    }, [] as TGroupOption[])
})

const availableSeatsStyling = computed(() => {
    if (remainingLicenses.value === 0 && emails.value.length === validEmails.value.length) {
        return 'zero-available'
    }
    if (remainingLicenses.value === 0 || remainingLicenses.value < 0) {
        return 'zero-error'
    }
    return 'available'
})

const isOutOfAvailableSeats = computed(() => {
    return remainingLicenses.value < 0
})

const emitClose = () => {
    emit('close', true)
}

const resetGroupTypeSelection = () => {
    if (selectedExamIds.value.length === 1 && groupOptions.value.length) {
        selectGroupType.value = groupTypeOptions[0] || null
        const currentGroup = groupModule.getters.getCurrentGroup()
        const currentGroupOption = groupOptions.value.find(groupOption =>
            groupOption.value === currentGroup?.objectId
        )
        selectedGroup.value = currentGroupOption ? currentGroupOption : null
    } else {
        selectGroupType.value = groupTypeOptions[1] || null
        selectedGroup.value = null
        addToGroup.value = false
    }
}

const checkIsDuplicateGroup = () => {
    if (!newGroupName.value) {
        isDuplicateGroup.value = false
        return
    }
    isDuplicateGroup.value = selectedExamIds.value.some(selectedExamId => {
        const examGuid = orgExamIdGuidLib.value[selectedExamId]
        const newGroupCompositeKey = `${examGuid}/${currentOrgInstructor.value?.objectId}/${newGroupName.value}`
        return newGroupCompositeKey in groupsByCompositeKey.value
            && groupsByCompositeKey.value[newGroupCompositeKey]?.length
    })
}

const changeActiveTab = (tabName: string) => {
    if (
        (tabName === 'Add Emails' && !selectedExamIds.value.length)
        || (tabName === 'Personalize & Preview' && !validEmails.value.length)
    ) {
        return
    }
    activeTabName.value = tabName
}

const nextTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === tabs.length - 1) {
        return
    }
    activeTabName.value = tabs[currentIndex + 1] as string
}

const previousTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === 0) {
        return
    }
    activeTabName.value = tabs[currentIndex - 1] as string
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const checkedExamsChanged = (checkedRowIds: string[]) => {
    selectedExamIds.value = checkedRowIds
    const isAdmin = currentOrgInstructor.value?.isAdmin
    const currentInstructorOption = instructorOptions.value.find(opt =>
        opt && (opt.value === currentOrgInstructor.value?.objectId)
    )
    if (!isAdmin && currentInstructorOption && checkedRowIds.length) {
        selectedInstructor.value = currentInstructorOption
    } else {
        selectedInstructor.value = null
    }
    resetGroupTypeSelection()
}

const submitAddStudents = async () => {
    const title = validEmails.value.length === 1
        ? `${validEmails.value.length} student invited`
        : `${validEmails.value.length} students invited`
    toastModule.actions.displayToast({ title })
    if (!currentOrganization.value) {
        throw new Error('submitAddInstructor: No current organization selected.')
    }

    isLoading.value = true
    try {
        await studentModule.actions.addStudents({
            organizationId: currentOrganization.value.objectId,
            orgExamIds: selectedExamIds.value,
            orgInstructorId: selectedInstructor.value?.value,
            emails: validEmails.value,
            customMessage: customMessage.value,
        })
        const examGuids = selectedExamIds.value.map(eid => {
            const exam = currentOrgExams.value[eid]
            return exam && examMetadata.value[exam.examMetadata.objectId]?.examGuid || ''
        })
        const bundles = selectedExamIds.value.map(id => bundleModule.getters.getExamBundle(id))
        analyticsModule.actions.amplitudeTrack(
            'Students Added',
            {
                studentsCount: validEmails.value.length,
                organizationId: currentOrganization.value.objectId,
                examGuids,
                examGuid: examGuids.length > 1 ? undefined : examGuids[0],
                bundleId: bundles[0]?.objectId,
                groupName: selectedGroup.value?.label || newGroupName.value || '',
            }
        )

        if (bundles.length > 1) {
            Sentry.captureException(
                'Seats assigned across multiple bundles',
                {
                    extra: {
                        bundleIds: bundles.map(b => b?.objectId),
                        organizationId: currentOrganization.value.objectId,
                        examGuids,
                    },
                }
            )
        }

        if (selectedGroup.value || newGroupName.value) {
            const currentOrgStudents = studentModule.getters.getCurrentOrgStudents()
            const orgStudentIds: string[] = []

            Object.values(currentOrgStudents).forEach(cos => {
                const pendingStudentLicenseEmail = currentOrgLicenses.value.find(col =>
                    col.orgStudent?.objectId === cos.objectId
                )?.userEmail as string

                if (validEmails.value.includes(pendingStudentLicenseEmail)) {
                    orgStudentIds.push(cos.objectId)
                }
            })

            if (orgStudentIds.length && selectedGroup.value) {
                studentModule.actions.addStudentsToGroup({
                    orgGroupId: selectedGroup.value.value,
                    orgStudentIds: orgStudentIds,
                })
            }
            if (newGroupName.value) {
                await groupModule.actions.addGroup({
                    ownerId: currentOrgInstructor.value?.objectId,
                    organizationId: currentOrganization.value.objectId,
                    orgStudentIds,
                    orgExamId: selectedExamIds.value[0] || '',
                    name: newGroupName.value,
                    type: usePermission.value?.value === 1 ? 'public' : 'private',
                    isPubliclyEditable: editPermission.value?.value === 1 ? true : false,
                })
            }
        }

        organizationModule.actions.validateOrgState()
        await userModule.actions.trackEvent('New_Student_Added', {
            numInvitedStudents: validEmails.value.length,
        })
    } catch (e) {
        const processedError = typeof e === 'string' ? new Error(e) : e
        Sentry.captureException(processedError)
    }
    isLoading.value = false
    emitClose()
}

watch(usePermission, (newVal) => {
    if (newVal?.value === 2) {
        editPermission.value = { value: 2, label: '' }
    }
}, { deep: true })

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchExamMetadata(),
        instructorModule.actions.fetchCurrentUserOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        groupModule.actions.fetchCurrentOrgGroups(),
        organizationModule.actions.fetchCurrentOrganization(),
    ])

    if (
        Object.values(currentOrgExams.value).length === 1
        && !disabledExamIds.value.length
    ) {
        selectedExamIds.value = [ Object.values(currentOrgExams.value)[0]?.objectId || '' ]
    }

    resetGroupTypeSelection()

    await userModule.actions.trackEvent('New_Student_Started')
})
</script>

<template>
    <PurchaseSeatsModal
        v-if="showPurchaseSeatsModal"
        @close="closePurchaseSeatsModal"
    />
    <Portal to="sidepanel">
        <SidePanel
            :tabs="tabs"
            :active-tab-name="activeTabName"
            :stepped-tabs="true"
            :isTeachSidePanel="true"
            name="Add Student(s)"
            class="add-students"
            @close="emitClose"
            @selectTab="changeActiveTab"
        >
            <template v-if="activeTabName === 'Select Exam'">
                <label>Select Exam</label>
                <p>What exam prep are you giving your student(s)?</p>
                <SeatsTable
                    :key="selectedExamIds.length"
                    class="add-students__seats"
                    :show-checkboxes="true"
                    theme="condensed"
                    :hide-archived-seats="true"
                    :initial-checked-exam-ids="selectedExamIds"
                    :disabled-exam-ids="disabledExamIds"
                    @checkedRowsChanged="checkedExamsChanged"
                />
                <PocketSelect
                    v-model="selectedInstructor"
                    label="Add Instructor (optional)"
                    :placeholder="selectedExamIds.length ? '' : 'Select Exam Prep First'"
                    :disabled="!selectedExamIds.length"
                    class="add-students__field"
                    :data="instructorOptions"
                    :show-empty-option="true"
                    :empty-option-label="'None'"
                />
                <div
                    class="add-students__toggle"
                >
                    <ToggleSwitch
                        :key="String(addToGroup)"
                        :disabled="selectedExamIds.length !== 1"
                        v-model="addToGroup"
                    />
                    <span
                        class="add-students__toggle-text"
                        :class="{
                            'add-students__toggle-text--disabled': selectedExamIds.length !== 1
                        }"
                    >
                        Add to Group
                    </span>
                </div>
                <div
                    v-if="addToGroup && selectedExamIds.length === 1"
                    class="add-students__toggled"
                >
                    <PocketRadio
                        v-model="selectGroupType"
                        :disabled="groupOptions.length === 0"
                        class="add-students__toggle-groups"
                        :data="groupTypeOptions"
                    />
                    <PocketSelect
                        v-if="selectGroupType && selectGroupType.value === 'existingGroup'"
                        v-model="selectedGroup"
                        label="Group Name"
                        class="add-students__field"
                        :data="groupOptions"
                        :show-empty-option="false"
                        placeholder="Select Group"
                        :style="{
                            'marginBottom': `${groupOptions.length * 48 + 40}px`
                        }"
                    >
                        <template #dropdownListItem="{ item }">
                            <div class="add-students__group-name">
                                {{ item.label }}
                            </div>
                            <div class="add-students__group-instructor" v-if="'instructor' in item">
                                {{ item.instructor }}
                            </div>
                        </template>
                    </PocketSelect>
                    <div
                        v-else-if="selectGroupType && selectGroupType.value === 'newGroup'"
                        class="add-students__new-group"
                    >
                        <PocketInput
                            v-model="newGroupName"
                            class="add-students__field"
                            field-type="text"
                            label="Group Name"
                            @focusout="checkIsDuplicateGroup"
                            @update:modelValue="clearIsDuplicateGroup"
                            :error="isDuplicateGroup"
                        />
                        <Errors
                            class="add-students__new-group-errors"
                            v-if="isDuplicateGroup"
                            :errors="['Group name already exists. Please try a different name.']"
                        />
                        <PocketRadio
                            v-model="usePermission"
                            label="Who can <strong>use</strong> it?"
                            class="add-students__use-permission"
                            :data="[
                                { label: 'Colleagues in organization', value: 1 },
                                { label: 'Just me', value: 2 }
                            ]"
                        />
                        <PocketRadio
                            v-model="editPermission"
                            label="Who can <strong>edit</strong> this group?"
                            class="add-students__edit-permission"
                            :disabled="!usePermission || usePermission.value !== 1"
                            :data="[
                                { label: 'Colleagues in organization', value: 1 },
                                { label: 'Just me', value: 2 }
                            ]"
                        />
                    </div>
                </div>
                <div
                    v-if="selectedExamIds.length > 1"
                    class="add-students__whats-next"
                >
                    <div
                        class="add-students__whats-next-text"
                    >
                        To add students to a group, only one exam may be selected.
                    </div>
                </div>
            </template>
            <template v-else-if="activeTabName === 'Add Emails'">
                <label>Add Emails</label>
                <p>Type or paste comma separated emails</p>
                <div
                    v-if="emails.length !== validEmails.length"
                    class="add-students__no-seats-top"
                >
                    <Icon type="warning" />
                    <span>Invalid Email(s)</span>
                </div>
                <div
                    v-if="remainingLicenses < 0"
                    class="add-students__no-seats-top add-students__no-seats-top-error"
                >
                    <!-- eslint-disable -->
                    <svg class="background" width="188px" height="19px" viewBox="0 0 188 19" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <g id="v2-Narrowing" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <g id="Add-Student-02-errors---Copy-&amp;-Paste-over-limit" transform="translate(-136.000000, -289.000000)" stroke="#FFECEC" stroke-width="14">
                                <path d="M138,300.5 C170.666667,294.5 232.333333,294.5 323,300.5" id="Path-7"></path>
                            </g>
                        </g>
                    </svg>
                    <!-- eslint-enable -->
                    <Icon type="warning" class="icon" /><span>Out of Available Seats</span>
                </div>
                <EmailTextarea
                    :key="JSON.stringify(emails)"
                    v-model="emails"
                    :available-seats="remainingLicenses"
                    :disabled-emails="usedEmails"
                    class="add-students__text-field"
                />
                <Table
                    v-if="mappedExams.length"
                    name=""
                    :rows="mappedExams"
                    :columns="examColumns"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    class="add-students__seats-table add-students__seats-table--condensed"
                    :table-column-label-styles="{
                        margin: '0 auto',
                        textAlign: 'center',
                        display: 'flex',
                        height: '30px',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                    }"
                    :table-cell-styles="{
                        alignItems: 'center',
                        display: 'flex',
                        margin: '0 auto',
                        width: '64px',
                        height: '30px'
                    }"
                    :table-sort-icon-styles="{
                        position: 'absolute', top: '50%', right: '-15px', marginTop: '-4.5px'
                    }"
                    @sort="updateSortSettings"
                >
                    <template #tableColumnLabelText="{ column }">
                        <div v-html="column.name" />
                    </template>
                    <template #tableCellValue="{ row, column }">
                        <div
                            v-if="column.propName === 'available'"
                            class="add-students__count"
                            :class="{ [`add-students__count--${availableSeatsStyling}`]: true }"
                            v-html="row[column.propName]"
                        />
                        <div
                            v-else-if="column.propName !== 'name'"
                            class="add-students__count"
                            :class="{ [`add-students__count--${column.propName}`]: true }"
                            v-html="row[column.propName as keyof typeof row]"
                        />
                    </template>
                </Table>
                <div
                    v-else-if="remainingLicenses < 0 && emails.length === validEmails.length"
                    class="add-students__overflow-seats"
                >
                    <div class="add-students__overflow-seats-title">
                        <Icon type="warning" /><span>Out of Available Seats</span>
                    </div>
                    <div class="add-students__overflow-seats-description">
                        {{ Math.abs(remainingLicenses) }}
                        Students cannot be invited. Talk with your admin to get more seats purchased.
                    </div>
                </div>
                <Errors
                    v-if="emailValidationErrors.length > 0"
                    :errors="emailValidationErrors"
                />
                <Errors v-if="isOutOfAvailableSeats">
                    <template #errors>
                        Out of available seats.
                        <PocketButton
                            type="tertiary"
                            @click="openPurchaseSeatsModal"
                        >
                            Purchase Seats
                        </PocketButton>
                        to add more students.
                    </template>
                </Errors>
            </template>
            <template v-else-if="activeTabName === 'Personalize & Preview'">
                <div class="add-students__personalize">
                    <label class="add-students__label">
                        Personalize
                    </label>
                    <p class="add-students__note">
                        Add a note to the invite email (optional)
                    </p>
                    <PocketTextarea
                        v-model="customMessage"
                        height="114px"
                        class="add-students__text-field"
                    />
                    <div class="add-students__whats-next">
                        <div class="add-students__whats-next-title">
                            What's next?
                        </div>
                        <div class="add-students__whats-next-text">
                            Students will receive an email with their license code. Once they activate their license,
                            you will see them move from 'Pending' to <br>
                            'Active Students' at the bottom of your settings tab.
                        </div>
                    </div>
                </div>
            </template>

            <template #action>
                <template v-if="activeTabName === 'Select Exam'">
                    <PocketButton
                        type="primary"
                        :disabled="!selectedExamIds.length
                            || isDuplicateGroup
                            || (
                                addToGroup
                                && !!selectGroupType
                                && ((
                                    selectGroupType.value === 'existingGroup'
                                    && !selectedGroup
                                ) || (
                                    selectGroupType.value === 'newGroup'
                                    && (!newGroupName || !usePermission || !editPermission)
                                ))
                            )
                        "
                        @click="nextTab"
                    >
                        Next <Icon type="arrow" class="add-students__next-arrow" />
                    </PocketButton>
                </template>
                <template v-else-if="activeTabName === 'Add Emails'">
                    <PocketButton
                        type="primary"
                        :disabled="!validEmails.length || !!emailValidationErrors.length"
                        @click="nextTab"
                    >
                        Next <Icon type="arrow" class="add-students__next-arrow" />
                    </PocketButton>
                </template>
                <template v-else-if="activeTabName === 'Personalize & Preview'">
                    <PocketButton
                        type="primary"
                        :disabled="!emails.length || !selectedExamIds.length || isLoading"
                        @click="submitAddStudents"
                    >
                        Add {{ newStudentsCount }} Student{{ newStudentsCount === 1 ? '' : 's' }}
                    </PocketButton>
                </template>
            </template>

            <template v-if="activeTabName !== 'Select Exam'" #details>
                <PocketButton
                    type="tertiary"
                    class="add-students__back"
                    @click="previousTab"
                >
                    <Icon type="arrow" class="add-students__back-arrow" />Back
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.add-students {
    :deep(*) {
        .uikit-side-panel__tab--stepper {
            margin-bottom: 0;
        }
        .uikit-side-panel__tab--stepper::after {
            bottom: 0;
        }
    }
    &__no-seats {
        background: rgba($sky-blue, 0.3);
        padding: 15px 34px 16px 13px;
        border-radius: 3px;
        margin: 0 0 15px 0;
    }

    &__no-seats-top {
        margin: -10px 0 -33px;
        display: flex;
        align-items: center;
        line-height: 19px;
        font-size: 14px;
        font-weight: 600;
        position: relative;

        &.add-students__no-seats-top-error {
            svg.background {
                position: absolute;
                left: -4px;
            }
        }

        svg.icon {
            height: 22px;
            position: relative;
            z-index: 1;
        }

        span {
            margin-left: 8px;
            position: relative;
            z-index: 1;
        }
    }

    &__overflow-seats-title,
    &__no-seats-title {
        line-height: 19px;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
        display: flex;
        align-items: center;

        svg {
            margin-right: 10px;
            height: 22px;
        }

        span {
            height: 19px;
        }
    }

    &__overflow-seats-description,
    &__no-seats-description {
        font-size: 14px;
        line-height: 22px;
    }

    &__overflow-seats {
        background: $red-hint;
        padding: 15px 30px 16px 13px;
        border-radius: 3px;
        margin-left: 0px;
    }

    &__seats-table {
        margin-bottom: 17px;
    }

    &__back-arrow {
        margin-right: 10px;
        transform: rotate(180deg);
    }

    &__field {
        margin: 34px 0 21px;
        max-width: 363px;
    }

    &__new-group-errors {
        margin-bottom: 21px;
    }

    &__use-permission {
        margin: 0 11px 20px;
    }

    &__edit-permission {
        margin-left: 11px;
    }

    &__label {
        font-weight: 600;
    }

    &__note {
        font-size: 14px;
        margin-top: 7px;
    }

    &__text-field {
        margin-top: 39px;
        margin-bottom: 10px;
    }

    &__preview-email {
        font-size: 13px;
        padding-left: 11.5px;
    }

    &__whats-next {
        position: relative;
        margin-top: 30px;
        padding: 14px 12px 12px 15px;
        background-color: rgba($sky-blue, 0.3);
        border-radius: 3px;
        border: 1px solid rgba($brand-blue, 0.4);
    }

    &__whats-next-title {
        font-weight: 600;
        font-size: 15px;
        margin-bottom: 9px;
        line-height: 20px;
    }

    &__whats-next-text {
        font-size: 14px;
        line-height: 19.12px;
    }

    &__text {
        font-size: 14px;
        line-height: 19.12px;
        margin-bottom: 14px;
    }

    &__next-arrow {
        margin-left: 6px;
    }

    &__seats {
        margin-bottom: 20px;
    }

    &__count {
        width: 64px;
        font-size: 16px;
        line-height: 19px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        border-radius: 3px;
        margin-top: 0;
        height: 30px;
    }

    &__count--available {
        background: $green-hint;
        color: $peacock-green;
    }

    &__count--zero-available {
        background: $white;
        border: 1px solid $aqua-oasis;
        color: $peacock-green;
    }

    &__count--zero-error {
        background: $white;
        border: 1px solid $brand-red;
    }

    &__count--pending {
        background: $manilla;
        color: $golden-hind;
    }

    &__count--active {
        background: $lovely-euphoric-delight;
        color: $barney-purple;
    }

    &__toggle {
        margin-top: 30px;
        display: flex;
    }

    &__toggle-text {
        font-size: 15px;
        margin-left: 9px;
        line-height: 21px;

        &--disabled {
            opacity: 0.5;
        }
    }

    &__toggle-groups {
        margin: 20px 0 0 16px;
    }

    &__groups-dropdown-entry {
        display: flex;
        justify-content: space-between;
        align-content: center;
        cursor: pointer;
        user-select: none;
        padding: 6px 13px;
        outline: none;

        &:hover {
            background: $gray-background;
        }
    }

    &__group-name {
        font-size: 15px;
        white-space: nowrap;
    }

    &__group-instructor {
        font-size: 13px;
        line-height: 18px;
        color: $slate-03;
    }
}
</style>
